import React from "react";

import { useChatContext } from "@/client/context/ChatContext";
import { useTheme } from "@/client/context/ThemeContext";
import {
  ChatIcon,
  DarkThemeIcon,
  ExportDbIcon,
  GroceryIcon,
  IngredientIcon,
  LightThemeIcon,
  PlanIcon,
  RecipeIcon,
  SessionIcon,
  StockIcon,
} from "@/client/icons";

interface NavItem {
  icon: React.ReactNode;
  label: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { icon: <ChatIcon />, label: "Chat", path: "/" },
  { icon: <RecipeIcon />, label: "Recetas", path: "/recetas" },
  { icon: <IngredientIcon />, label: "Ingredientes", path: "/ingredientes" },
  { icon: <PlanIcon />, label: "Plan semanal", path: "/plan" },
  { icon: <StockIcon />, label: "Stock", path: "/stock" },
  { icon: <SessionIcon />, label: "Sesiones", path: "/sesiones" },
  { icon: <GroceryIcon />, label: "Lista de compras", path: "/compras" },
];

interface SidebarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  onOpenPalette?: () => void;
  isOpen: boolean;
  onClose: () => void;
}

const THEME_CYCLE = ["system", "light", "dark"] as const;
const THEME_LABELS: Record<string, string> = { system: "Auto", light: "Claro", dark: "Oscuro" };

function ThemeToggle() {
  const { theme, setTheme, resolved } = useTheme();
  const idx = THEME_CYCLE.indexOf(theme);
  const next = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length] ?? "system";
  const Icon = resolved === "dark" ? DarkThemeIcon : LightThemeIcon;
  return (
    <button
      onClick={() => setTheme(next)}
      className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-term-text-muted hover:text-term-text hover:bg-term-raised rounded-lg cursor-pointer transition-all"
      title={`Tema: ${THEME_LABELS[theme]}`}
    >
      <Icon width={12} height={12} />
    </button>
  );
}

function SidebarContent({
  currentPath,
  onNavigate,
  onOpenPalette,
}: {
  currentPath: string;
  onNavigate: (path: string) => void;
  onOpenPalette?: () => void;
}) {
  const { downloadSql } = useChatContext();
  const activePath =
    currentPath === "/" || currentPath.startsWith("/c/")
      ? "/"
      : (NAV_ITEMS.find((item) => item.path !== "/" && currentPath.startsWith(item.path))?.path ??
        currentPath);

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-term-border">
        <span className="font-serif text-2xl text-term-accent">Meal Prep</span>
      </div>

      {/* Search trigger */}
      {onOpenPalette && (
        <div className="px-3 pt-3">
          <button
            onClick={onOpenPalette}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-term-text-muted bg-term-bg border border-term-border rounded-lg cursor-pointer hover:border-term-border-bright transition-colors"
          >
            <span className="flex-1 text-left">Buscar...</span>
            <kbd className="text-micro bg-term-raised border border-term-border rounded px-1 py-0.5 font-mono leading-none">
              ⌘K
            </kbd>
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 space-y-0.5">
        {NAV_ITEMS.map((item) => {
          const isActive = item.path === activePath;
          return (
            <button
              key={item.path}
              onClick={() => onNavigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all text-left${
                isActive
                  ? " bg-term-accent/10 text-term-accent font-medium"
                  : " text-term-text-dim hover:text-term-text hover:bg-term-raised"
              }`}
            >
              <span className="shrink-0 flex items-center">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.path === "/" && (
                <kbd className="text-micro text-term-text-muted bg-term-raised border border-term-border rounded px-1 py-0.5 font-mono leading-none">
                  ⌘J
                </kbd>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 py-3 border-t border-term-border flex items-center justify-between">
        <ThemeToggle />
        <button
          onClick={() => void downloadSql()}
          className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-term-text-muted hover:text-term-text hover:bg-term-raised rounded-lg cursor-pointer transition-all"
          title="Exportar SQL"
        >
          <ExportDbIcon width={12} height={12} />
          SQL
        </button>
      </div>
    </div>
  );
}

export function Sidebar({ currentPath, onNavigate, onOpenPalette, isOpen, onClose }: SidebarProps) {
  const handleNavigate = (path: string) => {
    onNavigate(path);
    onClose(); // close mobile sidebar after navigation
  };

  return (
    <>
      {/* Desktop sidebar — always visible */}
      <aside className="hidden md:flex flex-col w-52 shrink-0 border-r border-term-border bg-term-surface h-dvh sticky top-0 overflow-y-auto">
        <SidebarContent
          currentPath={currentPath}
          onNavigate={onNavigate}
          onOpenPalette={onOpenPalette}
        />
      </aside>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 w-72 z-50 bg-term-surface border-r border-term-border flex flex-col transition-transform duration-300 ease-in-out${
          isOpen ? " translate-x-0" : " -translate-x-full"
        }`}
      >
        <SidebarContent
          currentPath={currentPath}
          onNavigate={handleNavigate}
          onOpenPalette={onOpenPalette}
        />
      </div>
    </>
  );
}
